import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import AutocompleteInput from '@/components/base/AutocompleteInput';
import ItemEntryTable from '@/pages/billing/components/ItemEntryTable';
import { useToast } from '@/contexts/ToastContext';
import { emptyLineItem, calcTotals, type InvoiceLineItem } from '@/types/billing';
import { formatINR } from '@/utils/format';
import { salesOrderService } from '@/services/salesOrderService';
import { getData } from '@/services/FetchNodeServices.js';
import { getCustomers } from '@/api/party.api';

import { calcGST, round2 } from '@/utils/gst';

interface QuotationPrefill {
  fromQuotation: boolean;
  quotationId: string;
  quotationNumber?: string;
  customerId: string;
  customerName: string;
  customerGSTIN?: string | null;
  warehouseId?: string | null;
  paymentTerms?: string | null;
  deliveryTerms?: string | null;
  notes?: string | null;
  items: Array<{
    itemId?: string;
    itemCode?: string;
    itemName: string;
    hsnCode?: string;
    description?: string;
    qty: number;
    unit: string;
    unitId?: string;
    rate: number;
    discount?: number;
    taxRate?: number;
  }>;
}

import { useWarehouseStore } from '@/stores/warehouseStore';

export interface DynamicParty {
  id: string;
  name: string;
  type?: string;
  gstin?: string;
  billingAddress?: string;
  shippingAddress?: string;
  isActive?: boolean;
}

export interface DynamicWarehouse {
  id: string;
  name: string;
  is_active?: boolean;
  isActive?: boolean;
}

import DocumentNumberInput from '@/components/common/DocumentNumberInput';
import { updateSingleDocumentNumber, getNextDocumentNumber } from '@/services/documentNumberService';
import { carryLineUnit } from '@/hooks/useUnits';

export default function SalesOrderNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { selectedWarehouseId: topWarehouseId } = useWarehouseStore();
  const qtPrefill = (location.state as { fromQuotation?: QuotationPrefill } | null)?.fromQuotation;
  const editSO = (location.state as { editSO?: any } | null)?.editSO;
  const editId = (location.state as { soId?: string } | null)?.soId || editSO?.id;
  const isEditMode = !!(editSO || editId);

  const [soNumber, setSoNumber] = useState(editSO?.soNumber || '');
  const [selectedCustomer, setSelectedCustomer] = useState<DynamicParty | null>(() => {
    if (editSO) {
      return {
        id: editSO.customerId,
        name: editSO.customerName,
        gstin: editSO.customerGSTIN || undefined,
      };
    }
    if (!qtPrefill) return null;
    return {
      id: qtPrefill.customerId,
      name: qtPrefill.customerName,
      gstin: qtPrefill.customerGSTIN || undefined,
    };
  });
  const [customerQuery, setCustomerQuery] = useState(editSO?.customerName ?? qtPrefill?.customerName ?? '');
  const [date, setDate] = useState(editSO?.date ?? new Date().toISOString().split('T')[0]);
  const [expectedDelivery, setExpectedDelivery] = useState(editSO?.expectedDeliveryDate ?? '');
  const [paymentTerms, setPaymentTerms] = useState(editSO?.paymentTerms ?? qtPrefill?.paymentTerms ?? '');
  const [notes, setNotes] = useState(editSO?.notes ?? qtPrefill?.notes ?? '');
  const [warehouseId, setWarehouseId] = useState(editSO?.warehouseId ?? qtPrefill?.warehouseId ?? topWarehouseId ?? '');
  const [warehouses, setWarehouses] = useState<DynamicWarehouse[]>([]);
  const [saving, setSaving] = useState(false);

  // Fetch next SO number dynamically based on warehouse prefix when creating new
  useEffect(() => {
    if (isEditMode) return;
    async function fetchNext() {
      try {
        const res = await getNextDocumentNumber('SALES_ORDER', warehouseId);
        if (res?.data?.nextNumber) {
          setSoNumber(res.data.nextNumber);
        }
      } catch (err) {
        console.warn('Failed to fetch next SO number:', err);
      }
    }
    fetchNext();
  }, [warehouseId, isEditMode]);

  // Load warehouses from DB
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getData('api/v1/warehouse');
        const list: DynamicWarehouse[] = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setWarehouses(list);
        if (qtPrefill?.warehouseId && list.some((w) => w.id === qtPrefill.warehouseId)) {
          setWarehouseId(qtPrefill.warehouseId);
        } else if (topWarehouseId && list.some((w) => w.id === topWarehouseId)) {
          setWarehouseId(topWarehouseId);
        } else if (list.length > 0) {
          setWarehouseId((prev) => (prev && list.some((w) => w.id === prev) ? prev : list[0].id));
        }
      } catch (err) {
        console.warn('Failed to load warehouses from backend API:', err);
      }
    }
    loadWarehouses();
  }, [qtPrefill?.warehouseId, topWarehouseId]);

  // Load Sales Order for editing if isEditMode
  useEffect(() => {
    if (!isEditMode || !editId) return;
    async function loadSOForEdit() {
      try {
        const so: any = await salesOrderService.getSalesOrder(editId);
        if (!so) return;
        if (so.soNumber || so.so_number) setSoNumber(so.soNumber || so.so_number);
        if (so.date) setDate(so.date);
        if (so.expectedDeliveryDate || so.expected_delivery_date) {
          setExpectedDelivery(so.expectedDeliveryDate || so.expected_delivery_date);
        }
        if (so.warehouseId || so.warehouse_id) {
          setWarehouseId(so.warehouseId || so.warehouse_id);
        }
        if (so.customerId || so.customer_id) {
          setSelectedCustomer({
            id: so.customerId || so.customer_id,
            name: so.customerName || so.customer_name || '',
            gstin: so.customerGSTIN || so.customer_gstin || undefined,
            billingAddress: so.billingAddress || so.billing_address || '',
          });
          setCustomerQuery(so.customerName || so.customer_name || '');
        }
        if (so.paymentTerms || so.payment_terms) {
          setPaymentTerms(so.paymentTerms || so.payment_terms);
        }
        if (so.notes) setNotes(so.notes);

        if (so.items && so.items.length > 0) {
          setItems(
            so.items.map((i: any, idx: number) => {
              // As entered when another unit than the stock unit was used (2 Ton @ ₹80,000)
              const carried = carryLineUnit({ qty: Number(i.orderedQty || i.ordered_qty || i.qty || 1), rate: Number(i.rate || 0), enteredQty: i.enteredQty, enteredUnitId: i.enteredUnitId, enteredRate: i.enteredRate });
              const qty = carried.qty;
              const rate = carried.rate;
              const discount = Number(i.discount || 0);
              const taxRate = Number(i.taxRate || i.tax_rate || 18);
              const taxable = Number(i.taxableAmount || i.taxable_amount) || round2(qty * rate * (1 - discount / 100));
              const gst = calcGST(taxRate, taxable, true);
              return {
                ...emptyLineItem(),
                id: `so-item-${idx}`,
                itemId: i.itemId || i.item_id || '',
                itemName: i.itemName || i.item_name || '',
                hsnCode: i.hsnCode || i.hsn_code || '',
                qty,
                unit: i.unit || 'Pcs',
                unitId: i.unitId || i.unit_id || '',
                enteredUnitId: carried.enteredUnitId,
                rate,
                discount,
                taxRate,
                taxableAmount: taxable,
                ...gst,
                total: Number(i.totalAmount || i.total_amount) || round2(taxable + gst.cgst + gst.sgst + gst.igst),
              };
            })
          );
        }
      } catch (err) {
        console.warn('Failed to load full SO for edit:', err);
      }
    }
    loadSOForEdit();
  }, [isEditMode, editId]);

  // Pre-fetch customer detail if created from quotation
  useEffect(() => {
    if (qtPrefill && qtPrefill.customerName) {
      getData('api/v1/party', { search: qtPrefill.customerName })
        .then((res) => {
          const rawList = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
          if (rawList.length > 0) {
            const match =
              rawList.find(
                (p: any) =>
                  p.id === qtPrefill.customerId ||
                  p.name.toLowerCase() === qtPrefill.customerName.toLowerCase()
              ) || rawList[0];
            if (match) {
              setSelectedCustomer({
                id: match.id,
                name: match.name,
                gstin: match.gstin || match.customer_gstin || qtPrefill.customerGSTIN || undefined,
                billingAddress: match.billing_address || match.billingAddress || '',
                shippingAddress: match.shipping_address || match.shippingAddress || '',
                type: match.type,
                isActive: match.is_active ?? match.isActive ?? true,
              });
              setCustomerQuery(match.name);
            }
          }
        })
        .catch((err) => console.warn('Failed to pre-fetch customer detail:', err));
    }
  }, [qtPrefill?.customerId, qtPrefill?.customerName]);

  const [items, setItems] = useState<InvoiceLineItem[]>(() => {
    if (editSO && editSO.items && editSO.items.length > 0) {
      return editSO.items.map((i: any, idx: number) => {
        // As entered when another unit than the stock unit was used (2 Ton @ ₹80,000)
        const carried = carryLineUnit({ qty: Number(i.orderedQty || i.ordered_qty || i.qty || 1), rate: Number(i.rate || 0), enteredQty: i.enteredQty, enteredUnitId: i.enteredUnitId, enteredRate: i.enteredRate });
        const qty = carried.qty;
        const rate = carried.rate;
        const discount = Number(i.discount || 0);
        const taxRate = Number(i.taxRate || i.tax_rate || 18);
        const taxable = Number(i.taxableAmount || i.taxable_amount) || round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(taxRate, taxable, true);
        return {
          ...emptyLineItem(),
          id: `so-item-${idx}`,
          itemId: i.itemId || i.item_id || '',
          itemName: i.itemName || i.item_name || '',
          hsnCode: i.hsnCode || i.hsn_code || '',
          qty,
          unit: i.unit || 'Pcs',
          unitId: i.unitId || i.unit_id || '',
          enteredUnitId: carried.enteredUnitId,
          rate,
          discount,
          taxRate,
          taxableAmount: taxable,
          ...gst,
          total: Number(i.totalAmount || i.total_amount) || round2(taxable + gst.cgst + gst.sgst + gst.igst),
        };
      });
    }
    if (qtPrefill && qtPrefill.items.length > 0) {
      return qtPrefill.items.map((qi, idx) => {
        // Carried over in the unit the quotation was made in
        const carried = carryLineUnit(qi as Parameters<typeof carryLineUnit>[0]);
        const qty = carried.qty || 1;
        const rate = carried.rate || 0;
        const discount = qi.discount ?? 0;
        const taxRate = qi.taxRate ?? 18;
        const taxable = round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(taxRate, taxable, true);
        return {
          ...emptyLineItem(),
          id: `so-item-${idx}`,
          itemId: qi.itemId || '',
          itemCode: qi.itemCode || '',
          itemName: qi.itemName,
          hsnCode: qi.hsnCode || '',
          qty,
          unit: qi.unit || 'Pcs',
          unitId: qi.unitId || '',
          enteredUnitId: carried.enteredUnitId,
          rate,
          discount,
          taxRate,
          taxableAmount: taxable,
          ...gst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
        };
      });
    }
    return [emptyLineItem()];
  });

  const totals = useMemo(() => calcTotals(items), [items]);

  // Dynamic customer search from DB strictly for selected warehouse
  const fetchParties = useCallback(async (q: string): Promise<DynamicParty[]> => {
    try {
      const res = await getCustomers({ search: q, warehouse_id: warehouseId || undefined, isActive: true });
      if (res && res.data && Array.isArray(res.data)) {
        return res.data.map((p: any) => ({
          id: p.id,
          name: p.name,
          gstin: p.gstin || p.customer_gstin,
          billingAddress: p.billing_address || p.billingAddress,
          shippingAddress: p.shipping_address || p.shippingAddress,
          type: p.type,
          isActive: p.is_active ?? p.isActive ?? true,
        }));
      }
      return [];
    } catch (err) {
      console.warn('Failed to fetch customers from API:', err);
      return [];
    }
  }, [warehouseId]);

  const handleCustomerSelect = useCallback((p: DynamicParty) => {
    setSelectedCustomer(p);
    setCustomerQuery(p.name);
  }, []);

  // Check if quotation is already converted
  useEffect(() => {
    if (qtPrefill?.quotationId) {
      salesOrderService.listSalesOrders()
        .then((res) => {
          if (Array.isArray(res)) {
            const existing = res.find((so: any) => so.quotationId === qtPrefill.quotationId);
            if (existing) {
              toast.info(`Quotation ${qtPrefill.quotationNumber || ''} is already converted to Sales Order ${existing.soNumber || ''}`);
              navigate(`/sales/orders/${existing.id}`, { replace: true });
            }
          }
        })
        .catch(() => {});
    }
  }, [qtPrefill?.quotationId, qtPrefill?.quotationNumber, navigate, toast]);

  const handleSave = async (status: 'DRAFT' | 'CONFIRMED') => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return; }
    const validItems = items.filter((i) => i.itemName && i.qty > 0);
    if (validItems.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);

    try {
      const payload: SalesOrderPayload = {
        soNumber: soNumber.trim() || undefined,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        customerGSTIN: selectedCustomer.gstin || undefined,
        quotationId: qtPrefill?.quotationId || undefined,
        warehouseId: warehouseId || undefined,
        date,
        expectedDeliveryDate: expectedDelivery || undefined,
        status,
        notes: notes || undefined,
        paymentTerms: paymentTerms || undefined,
        totalAmount: totals.grandTotal,
        deliveredAmount: 0,
        pendingAmount: totals.grandTotal,
        items: validItems.map((i) => ({
          itemId: i.itemId || 'itm-gen',
          itemName: i.itemName,
          hsnCode: i.hsnCode,
          orderedQty: i.qty,
          deliveredQty: 0,
          unit: i.unit || 'Pcs',
          unitId: i.unitId,
          // qty and rate are in this unit; the server converts them to the stock unit
          enteredUnitId: i.enteredUnitId || undefined,
          rate: i.rate,
          discount: i.discount,
          taxRate: i.taxRate,
          taxableAmount: i.taxableAmount,
          totalAmount: i.total,
        })),
      };

      if (isEditMode && editId) {
        await salesOrderService.updateSalesOrder(editId, payload);
        toast.success(`Sales Order ${payload.soNumber || ''} updated successfully!`);
      } else {
        const created = await salesOrderService.createSalesOrder(payload);

        // Update Quotation status to CONVERTED_TO_SO if created from a quotation
        if (qtPrefill?.quotationId) {
          try {
            const { quotationService } = await import('@/services/quotationService');
            await quotationService.updateStatus(qtPrefill.quotationId, 'CONVERTED_TO_SO', {
              convertedToSOId: created.id,
              convertedToSONumber: created.soNumber,
            });
          } catch (e) {
            console.warn('Failed to update quotation status:', e);
          }
        }

        toast.success(`Sales Order ${created.soNumber || payload.soNumber || ''} ${status === 'DRAFT' ? 'saved as draft' : 'confirmed'}!`);
      }

      navigate('/sales/orders');
    } catch (err: any) {
      console.error('Failed to save sales order:', err);
      toast.error(err?.message || 'Error saving Sales Order to database');
    } finally {
      setSaving(false);
    }
  };

  const fl = 'w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700';
  const lb = 'block text-xs font-semibold text-slate-500 mb-1';

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6 pb-16">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold text-slate-800">
                  {isEditMode ? `Edit Sales Order ${soNumber ? `(${soNumber})` : ''}` : 'New Sales Order'}
                </h1>
                {qtPrefill && (
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 font-medium">
                    <i className="ri-file-copy-line" />
                    Creating SO from {qtPrefill.quotationNumber || 'Quotation'}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">
                {isEditMode ? 'Modify sales order details and save changes to DB' : (soNumber || 'Auto-generated SO Number')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-draft-line text-xs" /> Save as Draft
              </button>
              <button
                onClick={() => handleSave('CONFIRMED')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                ) : (
                  <><i className="ri-check-line" /> {isEditMode ? 'Save & Confirm' : 'Confirm Order'}</>
                )}
              </button>
            </div>
          </div>

          {/* Section 1: Header fields */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Order Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className={lb}>Customer <span className="text-red-500">*</span></label>
                <AutocompleteInput<DynamicParty>
                  value={customerQuery}
                  onChange={setCustomerQuery}
                  onSelect={handleCustomerSelect}
                  fetchOptions={fetchParties}
                  placeholder="Search customer from database..."
                  renderOption={(p) => (
                    <div>
                      <span className="font-medium">{p.name}</span>
                      {p.gstin && <span className="text-xs text-slate-400 ml-2">{p.gstin}</span>}
                    </div>
                  )}
                  getOptionKey={(p) => p.id}
                  getOptionLabel={(p) => p.name}
                  minChars={1}
                />
              </div>

              <div>
                <label className={lb}>SO Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fl} />
              </div>
              <div>
                <label className={lb}>Expected Delivery</label>
                <input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} className={fl} />
              </div>
              <div className="md:col-span-2">
                <label className={lb}>Payment Terms</label>
                <input type="text" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={fl} placeholder="e.g. 30 days net" />
              </div>
            </div>
            {selectedCustomer && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                {selectedCustomer.gstin && <span className="mr-4">GSTIN: <strong>{selectedCustomer.gstin}</strong></span>}
                <span>{selectedCustomer.billingAddress}</span>
              </div>
            )}
            <div className="mt-3">
              <label className={lb}>Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={`${fl} h-20 resize-none`} placeholder="Order notes..." />
            </div>
          </div>

          {/* Section 2: Items table */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</h2>
              <span className="text-xs text-slate-400">{items.filter((i) => i.itemName).length} items</span>
            </div>
            <ItemEntryTable items={items} onChange={setItems} isSameState={true} warehouseId={warehouseId} rateType="sale" />
          </div>

          {/* Section 3: Summary */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-sm ml-auto">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Sales Order Info &amp; Summary</h2>

            <div className="mb-3">
              <DocumentNumberInput
                value={soNumber}
                onChange={setSoNumber}
                label="Sales Order Number"
              />
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200">
                ORDER
              </span>
            </div>

            <div className="space-y-2 text-sm border-t border-slate-100 pt-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-medium text-slate-700">{formatINR(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tax</span>
                <span className="font-medium text-slate-700">{formatINR(totals.totalCGST + totals.totalSGST + totals.totalIGST)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between">
                <span className="font-semibold text-slate-800">Grand Total</span>
                <span className="font-bold text-indigo-600 text-base">{formatINR(totals.grandTotal)}</span>
              </div>
              <div className="text-xs text-slate-400">
                {items.filter((i) => i.itemName).length} items
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}